import authService from './authService'
import logger from '@/utils/logger'
import { PRICING } from '@/utils/constants/pricing.js'

// Extract apiClient from authService
const { apiClient } = authService

/**
 * PaymentService for Olvidadme
 * 
 * Implementing Service Layer pattern from PATTERNS_FROM_BASKET_BASE.md
 * Connects with enterprise-ready backend payment system
 * 
 * Features:
 * - Stripe Checkout Session creation
 * - Payment success handling with registration integration
 * - Error handling with Spanish localization
 * - GDPR compliance for payment data
 * - Integration with existing backend endpoints
 * 
 * Backend Integration:
 * - POST /api/payments/create-session - Create Stripe session
 * - POST /api/auth/register-with-plan - Complete registration after payment
 */

/**
 * Create Stripe Checkout Session for plan subscription
 * 
 * @param {string} planTier - Service tier (olvido, olvido_postmortem, testamento)
 * @param {Object} metadata - Additional metadata for tracking
 * @param {Object} customUrls - Optional custom success/cancel URLs
 * @returns {Promise<Object>} Session creation result
 */
export const createCheckoutSession = async (planTier, metadata = {}, customUrls = {}) => {
  try {
    logger.info('Creating Stripe checkout session', { 
      planTier,
      source: metadata.source || 'web'
    })

    const response = await apiClient.post('/payments/create-session', {
      planTier,
      metadata: {
        source: 'web',
        timestamp: new Date().toISOString(),
        ...metadata
      },
      ...customUrls
    })

    if (response.data?.status === 'success') {
      logger.info('Stripe session created successfully', {
        sessionId: response.data.data.sessionId,
        planTier,
        url: response.data.data.url ? 'present' : 'missing'
      })

      return {
        success: true,
        data: response.data.data
      }
    } else {
      throw new Error('Invalid response format from payment API')
    }

  } catch (error) {
    logger.error('Failed to create Stripe session', {
      planTier,
      error: error.message,
      response: error.response?.data
    })

    // Handle specific error cases
    let userMessage = 'Error al crear la sesión de pago. Por favor, inténtalo de nuevo.'
    
    if (error.response?.status === 401) {
      userMessage = 'Debes estar autenticado para crear una sesión de pago.'
    } else if (error.response?.status === 429) {
      userMessage = 'Demasiados intentos. Por favor, espera un momento antes de intentar de nuevo.'
    } else if (error.response?.status === 400) {
      userMessage = error.response.data?.message || 'Datos de pago inválidos.'
    }

    return {
      success: false,
      error: userMessage,
      originalError: error.message
    }
  }
}

/**
 * Redirect to Stripe Checkout
 * 
 * @param {string} checkoutUrl - Stripe Checkout URL
 */
export const redirectToCheckout = (checkoutUrl) => {
  if (typeof window !== 'undefined') {
    window.location.href = checkoutUrl
  }
}

/**
 * Handle successful payment return from Stripe
 * Complete user registration after successful payment
 *
 * @param {string} sessionId - Stripe session ID
 * @param {string} planTier - Selected plan tier
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration result
 */
export const completeRegistrationAfterPayment = async (sessionId, planTier, userData) => {
  try {
    logger.info('Completing registration after successful payment', {
      sessionId,
      planTier,
      email: userData.email,
      contractSignatureId: userData.contractSignatureId
    })

    // Prepare registration data for register-with-plan endpoint
    // Transform gdprConsents (frontend format) to gdprConsent (backend format)
    const { gdprConsents, contractSignatureId, ...restUserData } = userData

    const registrationData = {
      // User data (without gdprConsents and contractSignatureId)
      ...restUserData,

      // Payment information
      stripeSessionId: sessionId,
      planTier,

      // Contract signature ID (Sprint 3 - Contract Signature)
      ...(contractSignatureId && { contractSignatureId }),

      // GDPR consent in backend format
      gdprConsent: {
        dataProcessing: gdprConsents?.dataProcessing ?? true,
        marketing: gdprConsents?.marketing ?? false,
        analytics: false,
        version: '1.0',
        language: 'es'
      },

      // Source tracking
      source: 'web'
    }

    const response = await apiClient.post('/auth/register-with-plan', registrationData)

    if (response.data?.status === 'success') {
      logger.info('Post-payment registration completed successfully', {
        userId: response.data.data.user._id,
        email: response.data.data.user.email,
        planTier: response.data.data.planInfo?.tier,
        requiresEmailVerification: response.data.data.requiresEmailVerification
      })

      return {
        success: true,
        data: response.data.data
      }
    } else {
      throw new Error('Invalid response format from registration API')
    }

  } catch (error) {
    logger.error('Failed to complete registration after payment', {
      sessionId,
      planTier,
      email: userData.email,
      error: error.message,
      response: error.response?.data
    })

    // Handle specific error cases
    let userMessage = 'Error al completar el registro. Por favor, contacta con soporte.'
    
    if (error.response?.status === 400) {
      const responseData = error.response.data
      if (responseData?.message?.includes('already exists')) {
        userMessage = 'Ya existe una cuenta con este email. Por favor, inicia sesión.'
      } else if (responseData?.message?.includes('payment')) {
        userMessage = 'No se pudo verificar el pago. Por favor, contacta con soporte.'
      } else {
        userMessage = responseData?.message || 'Datos de registro inválidos.'
      }
    } else if (error.response?.status === 409) {
      userMessage = 'Ya existe una cuenta con este email o DNI.'
    }

    return {
      success: false,
      error: userMessage,
      originalError: error.message,
      validationErrors: error.response?.data?.errors || []
    }
  }
}

/**
 * Get plan information for display
 * 
 * @param {string} planTier - Plan tier
 * @returns {Object} Plan information
 */
export const getPlanInfo = (planTier) => {
  const plans = {
    olvido: {
      name: PRICING.OLVIDO.name,
      price: PRICING.OLVIDO.price,
      urls: PRICING.OLVIDO.urlLimit,
      features: [
        'Análisis legal de viabilidad',
        'Hasta 5 URLs para gestionar',
        'Solicitudes GDPR profesionales',
        'Seguimiento del caso',
        'Soporte por email'
      ],
      description: 'Elimina tu información personal de internet con gestión profesional.'
    },
    olvido_postmortem: {
      name: PRICING.OLVIDO_POSTMORTEM.name,
      price: PRICING.OLVIDO_POSTMORTEM.price,
      urls: PRICING.OLVIDO_POSTMORTEM.urlLimit,
      features: [
        'Para personas fallecidas',
        'Hasta 5 URLs para gestionar',
        'Documentación legal específica',
        'Gestión de cuentas digitales',
        'Soporte dedicado'
      ],
      description: 'Eliminación de datos personales de personas fallecidas.'
    },
    testamento: {
      name: PRICING.TESTAMENTO.name,
      price: PRICING.TESTAMENTO.price,
      urls: PRICING.TESTAMENTO.urlLimit,
      features: [
        'Instrucciones detalladas para albacea',
        'Designación de albacea digital',
        'Inventario de cuentas digitales',
        'Deseos post-mortem documentados',
        'Documento legalmente válido'
      ],
      description: 'Planifica tu legado digital mientras vives.'
    }
  }

  return plans[planTier] || null
}

/**
 * Validate plan tier
 *
 * @param {string} planTier - Plan tier to validate
 * @returns {boolean} Is valid plan tier
 */
export const isValidPlanTier = (planTier) => {
  // Updated for new service model (Sprint 03)
  const validTiers = ['olvido', 'olvido_postmortem', 'testamento']
  return validTiers.includes(planTier)
}

/**
 * Get user payment history with pagination
 * Sprint 03: Frontend Servicios
 *
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 10)
 * @param {string} options.status - Filter by status (optional)
 * @returns {Promise<Object>} Payments list with pagination
 */
export const getPaymentHistory = async (options = {}) => {
  try {
    const { page = 1, limit = 10, status = null } = options

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    })

    if (status) {
      params.append('status', status)
    }

    const response = await apiClient.get(`/payments/history?${params}`)

    const data = response.data.data

    // Normalize pagination: support both legacy flat format and current nested format
    const pagination = data.pagination ?? {
      page: data.page ?? page,
      limit: data.limit ?? limit,
      total: data.total ?? data.count ?? 0,
      totalPages: Math.ceil((data.total ?? data.count ?? 0) / (data.limit ?? limit)),
      hasNext: false,
      hasPrev: page > 1
    }

    logger.info('Payment history fetched', {
      count: data.payments.length,
      total: pagination.total,
      page
    })

    return {
      success: true,
      payments: data.payments,
      pagination
    }
  } catch (error) {
    logger.error('Error fetching payment history', { error: error.message })

    if (error.response?.status === 401) {
      throw new Error('Sesión expirada. Por favor, inicia sesión de nuevo.')
    }

    throw new Error(
      error.response?.data?.message ||
      'Error al obtener el historial de pagos'
    )
  }
}

/**
 * Get payment status badge config
 * Sprint 03: Frontend Servicios
 *
 * @param {string} status - Payment status
 * @returns {Object} Badge config with color and label
 */
export const getPaymentStatusBadge = (status) => {
  const statusConfig = {
    pending: { color: 'yellow', label: 'Pendiente' },
    paid: { color: 'green', label: 'Pagado' },
    failed: { color: 'red', label: 'Fallido' },
    cancelled: { color: 'gray', label: 'Cancelado' }
  }

  return statusConfig[status] || { color: 'gray', label: status }
}

/**
 * Format price for display
 * 
 * @param {number} price - Price in euros
 * @returns {string} Formatted price
 */
export const formatPrice = (price) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

// Default export for backwards compatibility
const paymentService = {
  createCheckoutSession,
  redirectToCheckout,
  completeRegistrationAfterPayment,
  getPlanInfo,
  isValidPlanTier,
  formatPrice,
  getPaymentHistory,
  getPaymentStatusBadge
}

export default paymentService